/**
 * Report Export Utilities
 * Professional design matching the sales invoice PDF style.
 * Supports: CSV, Excel (XLSX), PDF (jsPDF), and Print-optimized HTML views
 * Uses whitelabel branding and logo from tenant settings.
 */
import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { fmtCurrency, fmtDate, fmtNumber, getLocaleSettings } from './localeUtils';
import { publicImageUrl } from './media';

applyPlugin(jsPDF);

// ─── Branding ────────────────────────────────────────────
function getBranding() {
  try {
    const s = JSON.parse(localStorage.getItem('pharmxai_settings') || '{}');
    return {
      name: s.business_name || 'DutiLyne',
      tagline: s.tagline || 'AI-Powered Pharmacy Management',
      color: s.primary_color || '#14b8a6',
      logo_url: publicImageUrl(s.logo_url || ''),
      address: s.business_address || '',
      phone: s.contact_phone || '',
      email: s.contact_email || '',
    };
  } catch {
    return { name: 'DutiLyne', tagline: 'AI-Powered Pharmacy Management', color: '#14b8a6', logo_url: '', address: '', phone: '', email: '' };
  }
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [20, 184, 166];
}

// Loads the tenant logo as a data URL so jsPDF can embed it in the downloaded
// PDF — mirrors the logo already shown in the print-optimized report view.
// Resolves to null (never throws) so a missing/unreachable logo never blocks
// a report export.
async function loadLogo(url) {
  if (!url) return null;
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const ratio = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img.width / (img.height || 1));
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
    if (!ratio) return null;
    const format = blob.type.includes('png') ? 'PNG' : blob.type.includes('webp') ? 'WEBP' : 'JPEG';
    return { dataUrl, format, ratio };
  } catch {
    return null;
  }
}

const VISIBLE_COLS = (row) => Object.keys(row).filter(k => k !== 'bold' && k !== 'info');
const FMT_HEADER = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function isCurrencyCol(key) {
  // Whole-token match (split on `_`) so a count-ish word never false-positives
  // inside an unrelated currency field — e.g. "count" must not match "discount".
  const tokens = key.split('_');
  const countTokens = ['transactions', 'units', 'count', 'counts', 'quantity', 'qty', 'headcount', 'records', 'sold', 'number'];
  if (tokens.some(t => countTokens.includes(t))) return false;
  const markers = ['amount', 'revenue', 'cost', 'profit', 'billed', 'total', 'copay', 'claim', 'discount', 'spent', 'value', 'cash', 'credit', 'insurance', 'corporate', 'other', 'net', 'variance', 'impact', 'loss', 'price'];
  return markers.some(m => key.includes(m));
}

function formatCell(val, key) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'number') {
    if (isCurrencyCol(key)) return fmtCurrency(val);
    if (key.includes('percentage') || key.includes('margin') || key.includes('rate')) return `${val}%`;
    return fmtNumber(val);
  }
  if (key === 'date' || key === 'period' || key === 'expiry_date' || key === 'last_active') {
    return fmtDate(val) || val;
  }
  return String(val);
}

// PDF-safe text: replaces unicode currency symbols that jsPDF Helvetica can't render
function pdfSafe(text) {
  if (typeof text !== 'string') text = String(text ?? '');
  return text
    .replace(/GH₵/g, 'GHC')
    .replace(/₵/g, 'C')
    .replace(/₦/g, 'N')
    .replace(/৳/g, 'T')
    .replace(/₹/g, 'Rs');
}

function formatCellPDF(val, key) {
  return pdfSafe(formatCell(val, key));
}

// Summary-card value: currency gets the locale currency format, any other
// number gets locale-aware thousands separators (not a bare String(v)),
// everything else passes through unchanged.
function fmtTotal(v, k) {
  if (typeof v === 'number') return isCurrencyCol(k) ? fmtCurrency(v) : fmtNumber(v);
  return String(v ?? '');
}

function rawVal(val) {
  if (val === null || val === undefined) return '';
  return val;
}

// Escapes text dropped into the print-window HTML (names/addresses are staff-typed).
const esc = (val) => String(val ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// ─── CSV Export ──────────────────────────────────────────
export function exportCSV(rows, reportLabel) {
  if (!rows?.length) return;
  const cols = VISIBLE_COLS(rows[0]);
  const header = cols.map(FMT_HEADER).join(',');
  const body = rows.map(r => cols.map(c => {
    let v = rawVal(r[c]);
    // Spreadsheets treat a leading =,+,-,@ as a formula — neutralise it.
    if (typeof v === 'string' && /^[=+\-@]/.test(v)) v = `'${v}`;
    if (typeof v === 'string' && /[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
    return v;
  }).join(',')).join('\n');
  download(header + '\n' + body, `${reportLabel}.csv`, 'text/csv');
}

// ─── Excel Export ────────────────────────────────────────
export function exportExcel(rows, reportLabel, totals) {
  if (!rows?.length) return;
  const cols = VISIBLE_COLS(rows[0]);
  const headers = cols.map(FMT_HEADER);
  const wsData = [headers, ...rows.map(r => cols.map(c => rawVal(r[c])))];
  if (totals) {
    wsData.push([]);
    wsData.push(['Summary']);
    Object.entries(totals).forEach(([k, v]) => {
      wsData.push([FMT_HEADER(k), typeof v === 'number' ? v : String(v)]);
    });
  }
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  ws['!cols'] = cols.map(c => ({
    wch: Math.max(FMT_HEADER(c).length, ...rows.slice(0, 50).map(r => String(rawVal(r[c])).length), 10) + 2
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, reportLabel.slice(0, 31));
  XLSX.writeFile(wb, `${reportLabel}.xlsx`);
}

// ─── PDF Export (Invoice-style design) ───────────────────
export async function exportPDF(rows, reportLabel, totals, summaryByGroup) {
  if (!rows?.length) return;
  const brand = getBranding();
  const rgb = hexToRgb(brand.color);
  const cols = VISIBLE_COLS(rows[0]);
  const logo = await loadLogo(brand.logo_url);

  const doc = new jsPDF({ orientation: cols.length > 6 ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // ── Header (Invoice-style: logo + brand name + accent line) ──
  let textX = 14;
  if (logo) {
    try {
      const h = 16;
      const w = Math.min(h * logo.ratio, 40); // cap so a very wide logo can't crowd the REPORT block
      doc.addImage(logo.dataUrl, logo.format, 14, 8, w, h);
      textX = 14 + w + 4;
    } catch {
      textX = 14;
    }
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  doc.text(pdfSafe(brand.name), textX, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(pdfSafe(brand.tagline), textX, 22);
  if (brand.address) doc.text(pdfSafe(brand.address), textX, 27);

  const contactLine = pdfSafe([brand.phone, brand.email].filter(Boolean).join(' | '));
  if (contactLine) {
    doc.setFontSize(8);
    doc.text(contactLine, textX, brand.address ? 31 : 27);
  }

  // Right side: report type + date
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(50, 50, 50);
  doc.text('REPORT', pageW - 14, 14, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageW - 14, 20, { align: 'right' });
  doc.text(`${rows.length} record${rows.length !== 1 ? 's' : ''}`, pageW - 14, 25, { align: 'right' });

  // Accent line
  const accentY = brand.address ? 35 : 31;
  doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
  doc.setLineWidth(0.8);
  doc.line(14, accentY, pageW - 14, accentY);

  // Report title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(30, 30, 30);
  doc.text(FMT_HEADER(reportLabel.replace(/-/g, '_')), 14, accentY + 8);

  let startY = accentY + 14;

  // ── Summary Cards ──
  if (totals) {
    const entries = Object.entries(totals);
    const maxCards = Math.min(entries.length, 6);
    const cardW = Math.min(48, (pageW - 28 - (maxCards - 1) * 3) / maxCards);
    entries.slice(0, maxCards).forEach(([k, v], i) => {
      const x = 14 + i * (cardW + 3);
      doc.setDrawColor(220, 220, 220);
      doc.setFillColor(249, 250, 251);
      doc.roundedRect(x, startY, cardW, 15, 1.5, 1.5, 'FD');
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(6);
      doc.text(FMT_HEADER(k).toUpperCase(), x + 3, startY + 5.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(17, 24, 39);
      doc.setFontSize(9);
      const fmtVal = pdfSafe(fmtTotal(v, k));
      doc.text(fmtVal, x + 3, startY + 12);
    });
    startY += 22;
  }

  // ── Sub-table ──
  if (summaryByGroup?.length) {
    const subCols = Object.keys(summaryByGroup[0]);
    doc.autoTable({
      startY,
      head: [subCols.map(FMT_HEADER)],
      body: summaryByGroup.map(r => subCols.map(c => formatCellPDF(r[c], c))),
      styles: { fontSize: 7.5, cellPadding: 3, lineColor: [229, 231, 235] },
      headStyles: { fillColor: [rgb[0], rgb[1], rgb[2]], textColor: 255, fontStyle: 'bold', fontSize: 7.5, cellPadding: 3 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      margin: { left: 14, right: 14 },
    });
    startY = doc.lastAutoTable.finalY + 8;
  }

  // ── Main Data Table ──
  doc.autoTable({
    startY,
    head: [cols.map(FMT_HEADER)],
    body: rows.map(r => cols.map(c => formatCellPDF(r[c], c))),
    styles: { fontSize: 7.5, cellPadding: 3, lineColor: [229, 231, 235], lineWidth: 0.1 },
    headStyles: { fillColor: [rgb[0], rgb[1], rgb[2]], textColor: 255, fontStyle: 'bold', fontSize: 7.5, cellPadding: 3 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: 14, right: 14 },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const row = rows[data.row.index];
        if (row?.bold) data.cell.styles.fontStyle = 'bold';
        if (row?.info) data.cell.styles.textColor = [150, 150, 150];
      }
    },
  });

  // ── Footer on each page ──
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const h = doc.internal.pageSize.getHeight();
    // Accent line footer
    doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
    doc.setLineWidth(0.5);
    doc.line(14, h - 14, pageW - 14, h - 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(107, 114, 128);
    doc.text(pdfSafe(`${brand.name} — Confidential`), 14, h - 9);
    doc.text(`Page ${i} of ${pageCount}`, pageW - 14, h - 9, { align: 'right' });
  }

  doc.save(`${reportLabel}.pdf`);
}

// ─── Print-Optimized HTML (Invoice-style) ────────────────
// `signOff`: optional HTML string (already escaped by the caller) rendered
// just above the confidential footer — e.g. a signature/stamp line for
// registers that need a human sign-off at a regulator inspection.
export function printReport(rows, reportLabel, totals, summaryByGroup, signOff) {
  if (!rows?.length) return;
  const brand = getBranding();
  const cols = VISIBLE_COLS(rows[0]);

  const totalsHTML = totals ? `
    <div style="display:flex;flex-wrap:wrap;gap:12px;margin-bottom:24px;">
      ${Object.entries(totals).map(([k, v]) => {
        const fv = fmtTotal(v, k);
        return `<div style="border:1px solid #e5e7eb;border-radius:6px;padding:10px 16px;min-width:120px;background:#f9fafb;">
          <div style="font-size:9px;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;font-weight:600;">${esc(FMT_HEADER(k))}</div>
          <div style="font-size:18px;font-weight:700;color:#111827;margin-top:4px;">${esc(fv)}</div>
        </div>`;
      }).join('')}
    </div>
  ` : '';

  const subTableHTML = summaryByGroup?.length ? `
    <h3 style="font-size:13px;font-weight:600;margin:16px 0 8px;color:#111827;">Summary</h3>
    <table>
      <thead><tr>${Object.keys(summaryByGroup[0]).map(c => `<th>${esc(FMT_HEADER(c))}</th>`).join('')}</tr></thead>
      <tbody>${summaryByGroup.map(r => `<tr>${Object.entries(r).map(([k, v]) => `<td>${esc(formatCell(v, k))}</td>`).join('')}</tr>`).join('')}</tbody>
    </table>
  ` : '';

  const tableHTML = `
    <table>
      <thead><tr>${cols.map(c => `<th>${esc(FMT_HEADER(c))}</th>`).join('')}</tr></thead>
      <tbody>${rows.map(r => {
        const cls = r.bold ? 'font-weight:700;background:#f0f9ff;' : r.info ? 'color:#9ca3af;font-style:italic;' : '';
        return `<tr style="${cls}">${cols.map(c => `<td>${esc(formatCell(r[c], c))}</td>`).join('')}</tr>`;
      }).join('')}</tbody>
    </table>
  `;

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${FMT_HEADER(reportLabel.replace(/-/g, '_'))} — ${brand.name}</title>
<style>
  @page { size: ${cols.length > 6 ? 'landscape' : 'portrait'}; margin: 20mm; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .no-print { display: none; } }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif; font-size: 13px; color: #333; line-height: 1.5; margin: 0; padding: 20px; }
  .report-container { max-width: 1000px; margin: 0 auto; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid ${brand.color}; padding-bottom: 20px; margin-bottom: 24px; }
  .logo-section h1 { margin: 0; font-size: 28px; color: ${brand.color}; letter-spacing: -0.5px; }
  .logo-section p { margin: 4px 0 0; color: #666; font-size: 12px; }
  .logo-section .contact { font-size: 11px; color: #888; margin-top: 2px; }
  .report-meta { text-align: right; }
  .report-meta h2 { margin: 0; font-size: 20px; color: #333; text-transform: uppercase; letter-spacing: 2px; font-weight: 700; }
  .report-meta p { margin: 4px 0; font-size: 11px; color: #666; }
  .report-title { font-size: 16px; font-weight: 700; margin-bottom: 16px; color: #111827; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; page-break-inside: auto; }
  thead { background: ${brand.color}; }
  th { color: #fff; text-align: left; padding: 10px 10px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
  td { padding: 8px 10px; border-bottom: 1px solid #e5e7eb; font-size: 11px; }
  tbody tr:nth-child(even) { background: #f9fafb; }
  .footer { text-align: center; padding-top: 24px; border-top: 2px solid ${brand.color}; margin-top: 30px; color: #666; font-size: 11px; }
  .footer p { margin: 2px 0; }
  .print-btn { display: inline-block; background: ${brand.color}; color: #fff; padding: 10px 24px; border: none; border-radius: 6px; font-size: 14px; cursor: pointer; margin-bottom: 20px; }
  .print-btn:hover { opacity: 0.9; }
</style></head><body>
  <button class="no-print print-btn" onclick="window.print()">Print Report</button>
  <div class="report-container">
    <div class="header">
      <div class="logo-section">
        ${brand.logo_url ? `<img src="${brand.logo_url}" alt="Logo" style="max-height:50px;margin-bottom:8px;display:block;">` : ''}
        <h1>${brand.name}</h1>
        <p>${brand.tagline}</p>
        ${brand.address ? `<p class="contact">${brand.address}</p>` : ''}
        ${(brand.phone || brand.email) ? `<p class="contact">${[brand.phone ? `Tel: ${brand.phone}` : '', brand.email ? `Email: ${brand.email}` : ''].filter(Boolean).join(' | ')}</p>` : ''}
      </div>
      <div class="report-meta">
        <h2>Report</h2>
        <p>Generated: ${new Date().toLocaleString()}</p>
        <p>${rows.length} record${rows.length !== 1 ? 's' : ''}</p>
      </div>
    </div>
    <div class="report-title">${FMT_HEADER(reportLabel.replace(/-/g, '_'))}</div>
    ${totalsHTML}
    ${subTableHTML}
    ${tableHTML}
    ${signOff || ''}
    <div class="footer">
      <p>${brand.name} — Confidential</p>
      <p style="font-size:10px;color:#999;margin-top:4px;">This is a computer-generated report. ${new Date().toLocaleDateString()}</p>
    </div>
  </div>
</body></html>`;

  const w = window.open('', '_blank', 'width=1000,height=700');
  if (!w) { alert('Please allow popups to print the report'); return; }
  w.document.write(html);
  w.document.close();
  w.focus();
}

// ─── Helper ──────────────────────────────────────────────
function download(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
